/**
 * WebSocket client for real-time clipboard sync.
 * Uses environment variable for WebSocket URL - NO hardcoded values.
 */
import { config } from '../config';

export type WebSocketMessage = {
  type: string;
  action?: string;
  item_id?: string;
  data?: any;
  sync_version?: number;
  error_code?: string;
  message?: string;
};

export type WebSocketEventHandler = (data: any) => void;

export class ClipboardWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private isAuthenticated = false;
  private shouldReconnect = true;  // Flag to control reconnection behavior
  private isDisconnecting = false; // Prevent reconnect during intentional disconnect

  constructor(private token: string) {}

  connect(): void {
    // Don't connect if we're intentionally disconnecting or shouldn't reconnect
    if (this.isDisconnecting || !this.shouldReconnect) {
      return;
    }

    // SECURITY FIX: Don't send token in URL - send it in first message instead
    // This prevents token exposure in server logs, browser history, and referer headers
    const wsUrl = `${config.wsBaseUrl}/clipboard`;

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.isAuthenticated = false;

      // Send authentication message immediately after connection
      this.send({ type: 'auth', token: this.token });
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.ws.onclose = () => {
      this.attemptReconnect();
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    const { type, action, item_id, data, sync_version } = message;

    // Handle authentication responses
    if (type === 'auth_success') {
      this.isAuthenticated = true;
      return;
    }

    if (type === 'auth_failed') {
      console.error('WebSocket authentication failed:', message.error_code || message.message);
      // Prevent reconnection loop on auth failure - user needs to login again
      this.shouldReconnect = false;
      this.disconnect();
      return;
    }

    // Handle ping/pong
    if (type === 'ping') {
      this.send({ type: 'pong' });
      return;
    }

    // Trigger event handlers registered for this message type
    const handlers = this.eventHandlers.get(type) || [];
    handlers.forEach((handler) => handler({ action, item_id, data, sync_version }));
  }

  private attemptReconnect(): void {
    // Don't reconnect if we're intentionally disconnecting or auth failed
    if (this.isDisconnecting || !this.shouldReconnect) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  on(event: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  off(event: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  disconnect(): void {
    // Set flag to prevent reconnection during disconnect
    this.isDisconnecting = true;
    this.shouldReconnect = false;

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Clear event handlers
    this.eventHandlers.clear();
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
  }

  // Method to update token (e.g., after login with new user)
  updateToken(newToken: string): void {
    this.token = newToken;
    this.shouldReconnect = true;
    this.isDisconnecting = false;
    this.reconnectAttempts = 0;
  }
}
